import { supabase } from "@/integrations/supabase/client";
import { SearchResult } from "@/types/search";

/**
 * Hook to record search price history for analytics
 * Records all prices found during searches (OEM or REF) regardless of user authentication
 */
export const useSearchPriceRecording = () => {
  const recordPriceHistory = async (
    results: SearchResult[],
    searchQuery: string,
    searchType: 'oem' | 'ref'
  ) => {
    if (!results || results.length === 0) {
      return;
    }

    try {
      // Record-search-prices requires authentication (prevents anonymous flooding
      // of fabricated price data). Silently skip for anonymous visitors.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        return;
      }

      // Prepare price history records
      const priceRecords = results.map(result => ({
        reference: result.refNumber || result.oemReference || searchQuery,
        platform: result.platform,
        price: result.price?.current?.price || 0,
        search_type: searchType,
        search_query: searchQuery,
        brand: result.brand || null,
        product_title: result.title || result.name || null,
        is_in_stock: result.isInStock ?? true,
        found_at: new Date().toISOString(),
      }));

      // Send to edge function (service role); client cannot insert directly anymore.
      const { error } = await supabase.functions.invoke('record-search-prices', {
        body: { records: priceRecords },
      });

      if (error) {
        console.error('[Price Recording] Error recording price history:', error);
      } else {
        console.log(`[Price Recording] Successfully recorded ${priceRecords.length} prices for analytics`);
      }
    } catch (error) {
      console.error('[Price Recording] Exception in recordPriceHistory:', error);
    }
  };

  return { recordPriceHistory };
};
